import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Download, Users, Trophy, Target, TrendingUp, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/hooks/useAnalytics';

interface TeamPerformanceProps {
  data: TeamMember[];
  onExport?: () => void;
  isLoading?: boolean;
}

type SortBy = 'revenue' | 'deals' | 'conversion' | 'target';

export function TeamPerformance({
  data,
  onExport,
  isLoading = false,
}: TeamPerformanceProps) {
  const [sortBy, setSortBy] = useState<SortBy>('revenue');
  const [hoveredMember, setHoveredMember] = useState<string | null>(null);

  const sortedData = [...data].sort((a, b) => {
    switch (sortBy) {
      case 'revenue':
        return b.revenue - a.revenue;
      case 'deals':
        return b.dealsClosed - a.dealsClosed;
      case 'conversion':
        return b.conversionRate - a.conversionRate;
      case 'target':
        return (b.revenue / b.target) - (a.revenue / a.target);
      default:
        return 0;
    }
  });

  const chartData = sortedData.map((member) => ({
    ...member,
    targetAchievement: Math.min(100, Math.round((member.revenue / member.target) * 100)),
  }));

  const topPerformer = sortedData[0] || null;
  const totalRevenue = data.reduce((sum, m) => sum + (m.revenue || 0), 0);
  const totalDeals = data.reduce((sum, m) => sum + (m.dealsClosed || 0), 0);
  const avgConversion = data.length > 0 ? data.reduce((sum, m) => sum + (m.conversionRate || 0), 0) / data.length : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: TeamMember & { targetAchievement: number } }> }) => {
    if (active && payload && payload.length) {
      const member = payload[0].payload;
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-xl p-4 shadow-xl"
        >
          <div className="flex items-center gap-2 mb-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={member.avatar} alt={member.name} />
              <AvatarFallback className="bg-[#2B7FFF]/10 text-[#2B7FFF] text-xs">
                {member.name.split(' ').map((n) => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <span className="font-semibold text-foreground">{member.name}</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground">Revenue:</span>
              <span className="font-medium text-foreground">{formatCurrency(member.revenue)}</span>
            </div>
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground">Deals Closed:</span>
              <span className="font-medium text-foreground">{member.dealsClosed}</span>
            </div>
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground">Conversion:</span>
              <span className="font-medium text-foreground">{member.conversionRate}%</span>
            </div>
            <div className="flex items-center justify-between gap-8">
              <span className="text-muted-foreground">Target:</span>
              <span className="font-medium text-foreground">{member.targetAchievement}%</span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#2B7FFF]" />
            Team Performance
          </CardTitle>
          <CardDescription>Sales team metrics and targets</CardDescription>
        </div>
        <Button variant="outline" size="icon" onClick={onExport} className="h-9 w-9">
          <Download className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="p-3 rounded-xl bg-gradient-to-br from-[#2B7FFF]/5 to-[#2B7FFF]/10 border border-[#2B7FFF]/20"
          >
            <DollarSign className="h-4 w-4 text-[#2B7FFF] mb-1" />
            <p className="text-xs text-muted-foreground">Total Revenue</p>
            <p className="text-sm font-bold text-foreground">
              {isLoading ? '-' : formatCurrency(totalRevenue)}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="p-3 rounded-xl bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20"
          >
            <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400 mb-1" />
            <p className="text-xs text-muted-foreground">Total Deals</p>
            <p className="text-sm font-bold text-foreground">
              {isLoading ? '-' : totalDeals}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="p-3 rounded-xl bg-gradient-to-br from-amber-500/5 to-amber-500/10 border border-amber-500/20"
          >
            <Target className="h-4 w-4 text-amber-600 dark:text-amber-400 mb-1" />
            <p className="text-xs text-muted-foreground">Avg Conversion</p>
            <p className="text-sm font-bold text-foreground">
              {isLoading ? '-' : (isFinite(avgConversion) ? `${avgConversion.toFixed(1)}%` : '0%')}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25 }}
            className="p-3 rounded-xl bg-gradient-to-br from-purple-500/5 to-purple-500/10 border border-purple-500/20"
          >
            <Trophy className="h-4 w-4 text-purple-600 dark:text-purple-400 mb-1" />
            <p className="text-xs text-muted-foreground">Top Performer</p>
            <p className="text-sm font-bold text-foreground truncate">
              {isLoading ? '-' : (topPerformer?.name ? topPerformer.name.split(' ')[0] : '-')}
            </p>
          </motion.div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort by:</span>
          {(['revenue', 'deals', 'conversion', 'target'] as SortBy[]).map((option) => (
            <Button
              key={option}
              variant={sortBy === option ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSortBy(option)}
              className={cn(
                'h-7 text-xs capitalize',
                sortBy === option && 'bg-[#2B7FFF] hover:bg-[#1A5FCC]'
              )}
            >
              {option}
            </Button>
          ))}
        </div>

        {/* Team List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="h-[200px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
            </div>
          ) : (
            sortedData.map((member, index) => {
              const targetProgress = Math.min(100, (member.revenue / member.target) * 100);
              const isTopPerformer = index === 0;
              
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.08 }}
                  className={cn(
                    'p-4 rounded-xl border transition-all cursor-pointer',
                    'hover:shadow-md',
                    isTopPerformer 
                      ? 'bg-gradient-to-r from-amber-500/5 to-transparent border-amber-500/20' 
                      : 'bg-card border-border hover:border-[#2B7FFF]/30',
                    hoveredMember === member.id && 'ring-1 ring-[#2B7FFF]/30',
                    hoveredMember !== null && hoveredMember !== member.id && 'opacity-50'
                  )}
                  onMouseEnter={() => setHoveredMember(member.id)}
                  onMouseLeave={() => setHoveredMember(null)}
                >
                  <div className="flex items-start gap-4">
                    {/* Rank */}
                    <div className="flex flex-col items-center pt-1">
                      {isTopPerformer ? (
                        <motion.div
                          animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                          transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                        >
                          <Trophy className="h-5 w-5 text-amber-500" />
                        </motion.div>
                      ) : (
                        <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                      )}
                    </div>
                    
                    {/* Avatar */}
                    <Avatar className="h-10 w-10 ring-2 ring-border">
                      <AvatarImage src={member.avatar} alt={member.name} />
                      <AvatarFallback className="bg-[#2B7FFF]/10 text-[#2B7FFF]">
                        {member.name.split(' ').map((n) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{member.name}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {member.dealsClosed} deals
                            </span>
                            <Badge 
                              variant="secondary" 
                              className={cn(
                                'text-xs',
                                member.conversionRate >= 40 
                                  ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                  : member.conversionRate >= 30
                                  ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                  : 'bg-red-500/10 text-red-600 dark:text-red-400'
                              )}
                            >
                              {member.conversionRate}% conv.
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-foreground">{formatCurrency(member.revenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            of {formatCurrency(member.target)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="relative">
                        <Progress 
                          value={targetProgress} 
                          className="h-2"
                        />
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${targetProgress}%` }}
                          transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                          className={cn(
                            'absolute top-0 left-0 h-2 rounded-full',
                            targetProgress >= 100 
                              ? 'bg-green-500' 
                              : targetProgress >= 75 
                              ? 'bg-[#2B7FFF]'
                              : targetProgress >= 50
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                          )}
                          style={{ width: `${targetProgress}%` }}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          {targetProgress.toFixed(0)}% of target
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Horizontal Bar Chart */}
        {!isLoading && (
          <div className="h-[200px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  width={55}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: string) => value.split(' ')[0]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="revenue"
                  radius={[0, 4, 4, 0]}
                  fill="#2B7FFF"
                  animationDuration={1200}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index === 0 ? '#F59E0B' : '#2B7FFF'}
                      opacity={hoveredMember === null || hoveredMember === entry.id ? 1 : 0.4}
                      onMouseEnter={() => setHoveredMember(entry.id)}
                      onMouseLeave={() => setHoveredMember(null)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TeamPerformance;
